import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import styles from "../../styles/applicant/ApplicantTable.module.css";
import { setLanguage, currentLanguage } from '../../services/LanguageTranslationService';

import TableHeader from "./components/TableHeader";
import TableBody from "./components/TableBody";
import FilterMenu from "./components/FilterMenu";
import LanguageMenu from "./components/LanguageMenu";
import ColumnsMenu from "./components/ColumnsMenu";
import ApplicationDetailsModal from "./components/ApplicationDetailsModal";
import LoadingState from "./components/LoadingState";
import EmptyState from "./components/EmptyState";

import {
    INITIAL_TABLE_DATA,
    COLUMNS_CONFIG,
    FILTER_OPTIONS,
    UI_TEXT,
    DEFAULT_VISIBLE_COLUMNS
} from "./constants/applicantTableConstants";

const ApplicantTable = () => {
    const token = localStorage.getItem("token");
    const [searchQuery, setSearchQuery] = useState("");
    const [tableData, setTableData] = useState(INITIAL_TABLE_DATA);
    const [isLoading, setIsLoading] = useState(true);
    const [activeFilters, setActiveFilters] = useState([]);
    const [columnVisibility, setColumnVisibility] = useState(
        Object.keys(COLUMNS_CONFIG.en).reduce((acc, key) => {
            acc[key] = DEFAULT_VISIBLE_COLUMNS.includes(key);
            return acc;
        }, {})
    );
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'dateOfIssue', direction: 'desc' });
    const [language] = useState(currentLanguage);
    const [totalApplications, setTotalApplications] = useState(0);
    const [totalApprovedApplications, setTotalApprovedApplications] = useState(0);
    const [uploadingCertificate, setUploadingCertificate] = useState(false);
    const [uploadingCertificateMeasurement, setUploadingCertificateMeasurement] = useState(false);
    const [uploadingCertificateFee, setUploadingCertificateFee] = useState(false);
    const [uploadedCertificate, setUploadedCertificate] = useState(null);
    const [uploadedCertificateMeasurement, setUploadedCertificateMeasurement] = useState(null);
    const [uploadedCertificateFee, setUploadedCertificateFee] = useState(null);

    const currentConfig = useMemo(() => ({
        columns: COLUMNS_CONFIG[language],
        filters: FILTER_OPTIONS[language],
        uiText: UI_TEXT[language]
    }), [language]);

    const filterRef = useRef(null);
    const languageRef = useRef(null);
    const columnsRef = useRef(null);

    useEffect(() => {
        const fetchApplicationData = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/fetch_requests.php`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    credentials: 'include'
                });
                const result = await response.json();

                const formattedData = result.data.map(app => ({
                    request_unique_id: app.request_unique_id || "N/A",
                    senderID: app.sender_unique_id || "N/A",
                    dateOfIssue: app.created_at || "N/A",
                    dateOfAction: app.approved_at || "N/A",
                    id: app.id || "N/A",
                    division: app.division || "N/A",
                    district: app.district || "N/A",
                    taluka: app.taluka || "N/A",
                    village: app.village || "N/A",
                    applicantName: app.applicant_name || "N/A",
                    applicantAddress: app.applicant_address || "N/A",
                    applicantMobile: app.applicant_phone || "N/A",
                    applicantEmail: app.applicant_email || "N/A",
                    surveyNumber: app.survey_no || "N/A",
                    areaSqM: app.area || "N/A",
                    satbaraAttachment: app.attachment_pr_satbara || null,
                    meausurementAttachment: app.measurement_attachment_path || null,
                    certificatePath: app.upload_certificate_path || null,
                    certificatename: app.certification_name || "N/A",
                    confirm1: app.confirm1 || false,
                    confirm2: app.confirm2 || false,
                    feeReceipt: app.confirm_payment || false,
                    feeReceiptdocument: app.payment_receipt || null,
                    utrChequeNo: app.utrChequeNo || null,
                    status: app.status || "Pending",
                    remark: app.remark || "N/A",
                    zoneStatementValue: app.zone_statement_value || null,
                    approved_m_or_a: app.approved_m_or_a || null,
                    signature_path: app.signature_path || null,
                }));

                const approvedCount = result.data.filter(app => app.status === "approved" || app.status === 'approvemanully' || app.status === 'approveautomatically').length;

                setTotalApplications(result.data.length);
                setTotalApprovedApplications(approvedCount);
                setTableData(formattedData);
            } catch (error) {
                console.error("Error loading application data:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchApplicationData();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (activeDropdown) {
                let shouldClose = true;

                if (filterRef.current && filterRef.current.contains(event.target)) {
                    shouldClose = false;
                }
                if (languageRef.current && languageRef.current.contains(event.target)) {
                    shouldClose = false;
                }
                if (columnsRef.current && columnsRef.current.contains(event.target)) {
                    shouldClose = false;
                }

                if (shouldClose) {
                    setActiveDropdown(null);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activeDropdown]);

    const toggleFilterMenu = useCallback(() => {
        setActiveDropdown(prev => prev === 'filter' ? null : 'filter');
    }, []);

    const toggleLanguageMenull = useCallback(() => {
        console.log("active :", activeDropdown);
        setActiveDropdown(prev => prev === 'language' ? null : 'language');
        console.log("active :", activeDropdown);

    }, []);

    // Remove the click outside handler temporarily
    const toggleLanguageMenu = () => {
        setActiveDropdown(prev => prev === 'language' ? null : 'language');
    };

    const toggleColumnsMenu = useCallback(() => {
        setActiveDropdown(prev => prev === 'columns' ? null : 'columns');
    }, []);


    // Update the useEffect to handle clicks better
    // useEffect(() => {
    //     const handleClickOutside = (event) => {
    //         // If there's no active dropdown, do nothing
    //         if (!activeDropdown) return;

    //         // Check if click is inside any dropdown
    //         const isClickInsideDropdown = 
    //             (filterRef.current && filterRef.current.contains(event.target)) ||
    //             (languageRef.current && languageRef.current.contains(event.target)) ||
    //             (columnsRef.current && columnsRef.current.contains(event.target));

    //         // If click is outside all dropdowns, close the active one
    //         if (!isClickInsideDropdown) {
    //             setActiveDropdown(null);
    //         }
    //     };

    //     document.addEventListener('click', handleClickOutside);
    //     return () => {
    //         document.removeEventListener('click', handleClickOutside);
    //     };
    // }, [activeDropdown]);


    // const toggleFilterMenu = useCallback((e) => {
    //     e?.stopPropagation(); // Prevent event bubbling
    //     setActiveDropdown(prev => prev === 'filter' ? null : 'filter');
    // }, []);

    // const toggleLanguageMenu = useCallback((e) => {
    //     e?.stopPropagation(); // Prevent event bubbling
    //     setActiveDropdown(prev => prev === 'language' ? null : 'language');
    // }, []);

    // const toggleColumnsMenu = useCallback((e) => {
    //     e?.stopPropagation(); // Prevent event bubbling
    //     setActiveDropdown(prev => prev === 'columns' ? null : 'columns');
    // }, []);


    const displayApplications = useMemo(() => {
        if (!tableData.length) return [];

        const currentDate = new Date().toISOString().split("T")[0];
        const previousDate = new Date(new Date().setDate(new Date().getDate() - 1))
            .toISOString()
            .split("T")[0];
        const currentMonth = new Date().toISOString().slice(0, 7);

        return tableData.filter((app) => {
            const matchesSearch =
                app.applicantName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                app.surveyNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
                app.applicantMobile.includes(searchQuery);

            if (!matchesSearch) return false;
            if (activeFilters.length === 0) return true;

            const appDate = app.dateOfIssue.split(" ")[0];
            const certName = app.certificatename.toLowerCase().replace(/\s+/g, '_');

            return activeFilters.every(filter => {
                if (['pending', 'approved', 'rejected'].includes(filter)) {
                    return app.status.toLowerCase() === filter;
                }
                if (['zone', 'part_plan'].includes(filter)) {
                    return certName === filter;
                }
                switch (filter) {
                    case "today": return appDate === currentDate;
                    case "yesterday": return appDate === previousDate;
                    case "this_month": return appDate.slice(0, 7) === currentMonth;
                    default: return true;
                }
            });
        }).sort((a, b) => {
            if (!sortConfig.key) return 0;
            if (a[sortConfig.key] < b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (a[sortConfig.key] > b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [tableData, searchQuery, activeFilters, sortConfig]);

    const updateActiveFilters = useCallback((category, value) => {
        setActiveFilters(prev => {
            const otherCategoryFilters = prev.filter(filter => {
                const isDateFilter = ['today', 'yesterday', 'this_month'].includes(filter);
                const isStatusFilter = ['pending', 'approved', 'rejected'].includes(filter);
                const isServiceTypeFilter = ['zone', 'part_plan'].includes(filter);

                if (category === 'date' && !isDateFilter) return true;
                if (category === 'status' && !isStatusFilter) return true;
                if (category === 'ServiceType' && !isServiceTypeFilter) return true;
                return false;
            });

            return value === "all" ? otherCategoryFilters : [...otherCategoryFilters, value];
        });
    }, []);

    const toggleColumnDisplay = useCallback((columnName) => {
        setColumnVisibility(prev => ({
            ...prev,
            [columnName]: !prev[columnName]
        }));
    }, []);

    const openApplicationDetails = useCallback((application) => {
        setSelectedApplication(application);
        setIsDetailModalOpen(true);
    }, []);

    const closeApplicationDetails = useCallback(() => {
        setIsDetailModalOpen(false);
        setSelectedApplication(null);
        setUploadedCertificate(null);
        setUploadedCertificateMeasurement(null);
        setUploadedCertificateFee(null);
    }, []);

    const requestSort = useCallback((key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    }, []);

    const changeLanguage = useCallback((lang) => {
        setLanguage(lang);
        localStorage.setItem('preferredLanguage', lang);
        setActiveDropdown(null);
    }, []);

    const handleFieldChange = useCallback((field, value) => {
        setSelectedApplication(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    const uploadCertificate = useCallback((e) => {
        const file = e.target.files[0];
        if (file) {
            setUploadedCertificate(file);
        }
    }, []);

    const uploadCertificateMeasurement = useCallback((e) => {
        const fileMeasurement = e.target.files[0];
        if (fileMeasurement) {
            setUploadedCertificateMeasurement(fileMeasurement);
        }
    }, []);

    const uploadCertificateFee = useCallback((e) => {
        console.log('uploadCertificateFee : 112');
        const fileFee = e.target.files[0];
        if (fileFee) {
            console.log('uploadCertificateFee : 113 : ', fileFee);
            setUploadedCertificateFee(fileFee);
        }
    }, []);

    const handleResend = useCallback(async () => {
        if (!uploadedCertificate) {
            alert("Please Upload 7/12 extract");
            return;
        }

        if (!uploadedCertificateMeasurement) {
            alert("Please Upload Measurement Sheet");
            return;
        }

        // if (selectedApplication.certificatename === "Part Plan") {
        //     if (!uploadedCertificateMeasurement) {
        //         alert("Please Upload Measurement Sheet");
        //         return;
        //     }
        // }


        if (!uploadedCertificateFee) {
            alert("Please Upload Fee Recipt");
            return;
        }

        setUploadingCertificate(true);
        setUploadingCertificateMeasurement(true);
        setUploadingCertificateFee(true);

        const formData = new FormData();
        formData.append('certificate', uploadedCertificate);
        formData.append('certificateMeaurement', uploadedCertificateMeasurement);
        formData.append('uploadedCertificateFee', uploadedCertificateFee);
        formData.append('applicationId', selectedApplication.request_unique_id);
        formData.append('surveyNumber', selectedApplication.surveyNumber);
        formData.append('areaSqM', selectedApplication.areaSqM);
        formData.append('applicantId', selectedApplication.senderID);
        formData.append('certificateType', selectedApplication.certificatename);
        formData.append('utrChequeNo', selectedApplication.utrChequeNo);

        try {
            const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/resend_applicant_form.php`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                method: 'POST',
                body: formData,
                credentials: "include"
            });

            if (!response.ok) {
                throw new Error(await response.text());
            }

            const result = await response.json();

            setTableData(prevData =>
                prevData.map(app =>
                    app.id === selectedApplication.id
                        ? { ...app, certificatePath: result.filePath }
                        : app
                )
            );

            setSelectedApplication(prev => ({
                ...prev,
                certificatePath: result.filePath,
                status: "pending"
            }));

            console.log("result : ", result);
            alert('Application resent successfully.')
            closeApplicationDetails();
            window.location.reload();
        } catch (error) {
            console.error('Error resending application:', error);
        } finally {
            setUploadingCertificate(false);
            setUploadingCertificateMeasurement(false);
            setUploadingCertificateFee(false);
        }
    }, [uploadedCertificate, uploadedCertificateFee, uploadedCertificateMeasurement, selectedApplication, currentConfig.uiText, closeApplicationDetails]);

    const formatDate = useCallback((dateString) => {
        if (!dateString || dateString === "N/A") return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString(language === 'en' ? 'en-IN' : 'mr-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    }, [language]);

    return (
        <div className={styles.tableContainer}>
            <TableHeader
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                currentConfig={currentConfig}
                totalApprovedApplications={totalApprovedApplications}
                totalApplications={totalApplications}
                activeFilters={activeFilters}
                activeDropdown={activeDropdown}
                toggleFilterMenu={toggleFilterMenu}
                toggleLanguageMenu={toggleLanguageMenu}
                toggleColumnsMenu={toggleColumnsMenu}
                filterRef={filterRef}
                languageRef={languageRef}
                columnsRef={columnsRef}
            />

            {/* {activeDropdown === 'filter' && (
                <FilterMenu
                    filterRef={filterRef}
                    currentConfig={currentConfig}
                    activeFilters={activeFilters}
                    updateActiveFilters={updateActiveFilters}
                    setActiveFilters={setActiveFilters}
                    language={language}
                />
            )}

            {activeDropdown === 'language' && (
                <LanguageMenu
                    languageRef={languageRef}
                    currentConfig={currentConfig}
                    language={language}
                    changeLanguage={changeLanguage}
                />
            )}

            {activeDropdown === 'columns' && (
                <ColumnsMenu
                    columnsRef={columnsRef}
                    currentConfig={currentConfig}
                    columnVisibility={columnVisibility}
                    toggleColumnDisplay={toggleColumnDisplay}
                    language={language}
                />
            )} */}

            {isLoading ? (
                <LoadingState />
            ) : displayApplications.length === 0 ? (
                <EmptyState
                    currentConfig={currentConfig}
                    searchQuery={searchQuery}
                    activeFilters={activeFilters}
                />
            ) : (
                <table className={styles.table}>
                    <TableBody
                        currentConfig={currentConfig}
                        columnVisibility={columnVisibility}
                        sortConfig={sortConfig}
                        requestSort={requestSort}
                        displayApplications={displayApplications}
                        language={language}
                        formatDate={formatDate}
                        openApplicationDetails={openApplicationDetails}
                    />
                </table>
            )}

            {isDetailModalOpen && (
                <ApplicationDetailsModal
                    selectedApplication={selectedApplication}
                    closeApplicationDetails={closeApplicationDetails}
                    language={language}
                    formatDate={formatDate}
                    handleFieldChange={handleFieldChange}
                    uploadCertificate={uploadCertificate}
                    uploadCertificateMeasurement={uploadCertificateMeasurement}
                    uploadCertificateFee={uploadCertificateFee}
                    handleResend={handleResend}
                    uploadingCertificate={uploadingCertificate}
                    uploadingCertificateMeasurement={uploadingCertificateMeasurement}
                    uploadingCertificateFee={uploadingCertificateFee}
                />
            )}
        </div>
    );
};

export default ApplicantTable;